import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { toast } from 'sonner';
import { generateUUID } from '@/lib/utils';

export interface CustomDesign {
  id: string;
  imageUrl: string;
  prompt?: string;
  productId?: string;
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  createdAt: number;
}

interface CustomDesignContextType {
  designs: CustomDesign[];
  currentDesign: CustomDesign | null;
  addDesign: (design: Omit<CustomDesign, 'id' | 'createdAt'>) => CustomDesign;
  updateDesign: (id: string, updates: Partial<CustomDesign>) => void;
  deleteDesign: (id: string) => void;
  setCurrentDesign: (design: CustomDesign | null) => void;
  getDesignById: (id: string) => CustomDesign | undefined;
  clearDesigns: () => void;
}

const CustomDesignContext = createContext<CustomDesignContextType | undefined>(undefined);

const STORAGE_KEY = 'aaaprinters_custom_designs';

const loadDesignsFromStorage = (): CustomDesign[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load designs from storage:', error);
    return [];
  }
};

const saveDesignsToStorage = (designs: CustomDesign[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(designs));
  } catch (error) {
    console.error('Failed to save designs to storage:', error);
  }
};

export const CustomDesignProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [designs, setDesigns] = useState<CustomDesign[]>(loadDesignsFromStorage);
  const [currentDesign, setCurrentDesign] = useState<CustomDesign | null>(null);

  // Sync designs with Supabase when user logs in
  useEffect(() => {
    if (user) {
      syncDesignsWithDatabase();
    }
  }, [user]);

  const syncDesignsWithDatabase = async () => {
    if (!user) return;

    try {
      // Get designs from Supabase
      const { data: dbDesigns, error } = await supabase
        .from('user_designs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Convert database format to local format
      const localDesigns: CustomDesign[] = (dbDesigns || []).map(dbDesign => ({
        id: dbDesign.id,
        imageUrl: dbDesign.image_url,
        prompt: dbDesign.prompt || undefined,
        productId: dbDesign.product_id || undefined,
        position: dbDesign.position_x !== null && dbDesign.position_y !== null
          ? { x: dbDesign.position_x, y: dbDesign.position_y }
          : undefined,
        size: dbDesign.size_width !== null && dbDesign.size_height !== null
          ? { width: dbDesign.size_width, height: dbDesign.size_height }
          : undefined,
        createdAt: new Date(dbDesign.created_at).getTime(),
      }));

      // Merge with local storage designs (if any)
      const localStorageDesigns = loadDesignsFromStorage();
      const mergedDesigns = [...localDesigns];

      // Add local storage designs that don't exist in database
      for (const localDesign of localStorageDesigns) {
        if (!mergedDesigns.some(d => d.id === localDesign.id)) {
          // Upload local design to database
          await saveDesignToDatabase(localDesign);
          mergedDesigns.push(localDesign);
        }
      }

      setDesigns(mergedDesigns);
      saveDesignsToStorage(mergedDesigns);
    } catch (error) {
      console.error('Error syncing designs:', error);
    }
  };

  const saveDesignToDatabase = async (design: CustomDesign) => {
    if (!user) return;

    try {
      await supabase.from('user_designs').insert({
        id: design.id,
        user_id: user.id,
        image_url: design.imageUrl,
        prompt: design.prompt || null,
        product_id: design.productId || null,
        position_x: design.position?.x || null,
        position_y: design.position?.y || null,
        size_width: design.size?.width || null,
        size_height: design.size?.height || null,
      });
    } catch (error) {
      console.error('Error saving design to database:', error);
      throw error;
    }
  };

  const addDesign = useCallback(async (design: Omit<CustomDesign, 'id' | 'createdAt'>) => {
    // Generate proper UUID v4 for database compatibility
    const newDesign: CustomDesign = {
      ...design,
      id: generateUUID(), // Use proper UUID instead of custom string format
      createdAt: Date.now(),
    };

    // Save to local storage first
    setDesigns(prev => {
      const updated = [...prev, newDesign];
      saveDesignsToStorage(updated);
      return updated;
    });

    // Save to database if user is logged in
    if (user) {
      try {
        await saveDesignToDatabase(newDesign);
        toast.success('Design saved to your profile!');
      } catch (error) {
        console.error('Error saving to database:', error);
        toast.error('Design saved locally but failed to sync to cloud');
      }
    } else {
      toast.info('Design saved locally. Sign in to save to cloud!');
    }

    return newDesign;
  }, [user]);

  const updateDesign = useCallback((id: string, updates: Partial<CustomDesign>) => {
    setDesigns(prev => {
      const updated = prev.map(design =>
        design.id === id ? { ...design, ...updates } : design
      );
      saveDesignsToStorage(updated);
      return updated;
    });

    if (currentDesign?.id === id) {
      setCurrentDesign(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [currentDesign]);

  const deleteDesign = useCallback((id: string) => {
    setDesigns(prev => {
      const updated = prev.filter(design => design.id !== id);
      saveDesignsToStorage(updated);
      return updated;
    });

    if (currentDesign?.id === id) {
      setCurrentDesign(null);
    }
  }, [currentDesign]);

  const getDesignById = useCallback((id: string) => {
    return designs.find(design => design.id === id);
  }, [designs]);

  const clearDesigns = useCallback(() => {
    setDesigns([]);
    setCurrentDesign(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value: CustomDesignContextType = {
    designs,
    currentDesign,
    addDesign,
    updateDesign,
    deleteDesign,
    setCurrentDesign,
    getDesignById,
    clearDesigns,
  };

  return (
    <CustomDesignContext.Provider value={value}>
      {children}
    </CustomDesignContext.Provider>
  );
};

export const useCustomDesign = (): CustomDesignContextType => {
  const context = useContext(CustomDesignContext);
  if (!context) {
    throw new Error('useCustomDesign must be used within a CustomDesignProvider');
  }
  return context;
};